import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import "./employees.css";
import { useTranslation } from "react-i18next";
import { Employee, EmployeeFilterResource, EmployeeFilterResponse } from "@/models/employees";
import { Checkbox, IconButton, InputAdornment, MenuItem, Modal, styled } from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import popupService from "@/services/popupService";
import { EmployeeGroup, OrganizationUnit, PermissionName } from "@/models/apiModels";
import PlusElement from "@/assets/images/PlusElement.svg";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import authStore from "@/stores/authStore";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";
import BaseInput from "../common/baseInput";
import BaseTooltip from "../common/baseTooltip";
import { checkPermissionsWithAlert, checkPermissionsWithoutAlert, formatDate } from "@/util";
import BaseButton from "../common/baseButton";
import FilterCancelIcon from "../styled/filter.cancel.icon";
import BaseChangeStatus from "../common/baseChangeStatus";
import EmployeeInfoTab from "./employeeInfoTab/employeeInfoTab";
import CancelIcon from "@mui/icons-material/Cancel";
import filtersStore, { EmployeeFilterData } from "@/stores/filtersStore";
import TotalCountComponent from "@/components/common/resultCountComponent";
import ClearIcon from "@/assets/icons/clear-icon.svg";
import to from "await-to-js";
import EmployeesActionButton from "./employeesActionButton";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const StyledExcelButton = styled(IconButton)(
    () => `
        &:hover {
          background-color: transparent;
        }
      `
);

const Employees = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const createEmployeeURL = "/employees/createEmployee";
    const [employeeFilter, setEmployeeFilter] = useState<EmployeeFilterResource>({
        pageIndex: 0,
        orderBy: "",
        asc: false,
        keywords: "",
        firstName: "",
        lastName: "",
        organizationUnitId: null,
        employeeGroupId: null,
        employeePID: "",
        phone: "",
        employeeStatus: null,
    });
    const [hasNext, setHasNext] = useState(true);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [organizationUnits, setOrganizationUnits] = useState<OrganizationUnit[]>([]);
    const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [hiddenFilter, setHiddenFilter] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [filterTabsArray, setFilterTabsArray] = useState<Array<{ id: string; name: string; value: string }>>([]);
    const [searchCounter, setSearchCounter] = useState(0);
    const [openImportModal, setOpenImportModal] = useState(false);
    const elementIds = ["employee-info-tab", "employee-total-count", "employee-filter-block", "filter-tabs"];

    /// Checkbox actions state
    const [checkAll, setCheckAll] = useState<boolean>(false);
    const [checkedEmployees, setCheckedEmployees] = useState<number[]>([]);
    const [excludedEmployees, setExcludedEmployees] = useState<number[]>([]);

    function handleEmployeeStatusTab(employeeStatus: boolean | null) {
        if (employeeStatus == null) {
            return "";
        } else if (employeeStatus === true) {
            return t("employee.ActiveStatus");
        } else {
            return t("employee.InactiveStatus");
        }
    }

    function sendEmail(email: string) {
        window.location.href = `mailto:${email}`;
    }

    function getEmployeeGroup(groupId: string) {
        const group = employeeGroups?.find((group) => group.id === Number(groupId));
        return group?.text;
    }

    function getOrganizationUnit(organizationId: string) {
        const unit = organizationUnits?.find((unit) => unit.id === Number(organizationId));
        return unit?.text;
    }

    function validatePermissionAndNavigate(id: number | undefined) {
        if ((checkPermissionsWithAlert(PermissionName.EmployeeGeneralInfo), true)) {
            navigate("/employees/" + id + "/info");
        } else if ((checkPermissionsWithAlert(PermissionName.EmployeeDigitalWallet), true)) {
            navigate("/employees/" + id + "/wallet");
        } else if ((checkPermissionsWithAlert(PermissionName.EmployeeOrders), true)) {
            navigate("/employees/" + id + "/employeeOrders");
        } else if (checkPermissionsWithAlert(PermissionName.EditAttachedDoc)) {
            navigate("/employees/" + id + "/docs");
        } else {
            return;
        }
    }

    function openEmployeePage(id: number | undefined, employeeName: string) {
        if (!id) {
            return;
        }

        const employeeFilterData = new EmployeeFilterData();
        employeeFilterData.filter = employeeFilter;
        employeeFilterData.filterTabs = filterTabsArray;
        employeeFilterData.hiddenFilter = hiddenFilter;

        filtersStore.setEmployeeFilterData(employeeFilterData);
        employeeBreadcrumbStore.update(employeeName);
        validatePermissionAndNavigate(id);
    }

    function handleClearFilters() {
        while (filterTabsArray.length > 0) {
            const filterTab = filterTabsArray[0];
            removeFilterTab(filterTab.name, filterTab.id);
            filterTabsArray.splice(0, 1);
        }
        filtersStore.setEmployeeFilterData(null);
        setEmployeeFilter((prevData) => ({
            ...prevData,
            keywords: "",
            firstName: "",
            lastName: "",
            employeeGroupId: null,
            organizationUnitId: null,
            employeeStatus: null,
            employeePID: "",
            phone: "",
            orderBy: "",
            asc: false,
            pageIndex: 0,
        }));
        setCheckAll(false);
        setCheckedEmployees([]);
        setExcludedEmployees([]);
        setSelectedEmployee(null);
        setSearchCounter((prevCounter) => prevCounter + 1);
    }

    function handleKeyPressSearch(event: any) {
        if (event.key === "Enter") {
            onClickSearch();
        }
    }

    async function getEmployeesInitData() {
        const employeesListDataResponsePromise = api.getEmployeesPageData();
        const employeesListDataResponse = await employeesListDataResponsePromise;
        setOrganizationUnits(employeesListDataResponse.data.organizationUnits);
        setEmployeeGroups(employeesListDataResponse.data.employeeGroups);
    }

    const setFilterTabs = async () => {
        const filters = [
            {
                id: "keyword-input",
                name: employeeFilter.keywords,
                value: `${t("employee.QuickSearch")}:`,
            },
            {
                id: "first-name-input",
                name: employeeFilter.firstName,
                value: `${t("employee.FirstName")}:`,
            },
            {
                id: "last-name-input",
                name: employeeFilter.lastName,
                value: `${t("employee.LastName")}:`,
            },
            {
                id: "department-input",
                name: getOrganizationUnit(employeeFilter.organizationUnitId?.toString() ?? "") ?? "",
                value: `${t("employee.Department")}:`,
            },
            {
                id: "employee-group-input",
                name: getEmployeeGroup(employeeFilter.employeeGroupId?.toString() ?? "") ?? "",
                value: `${t("employee.EmployeeGroup")}:`,
            },
            {
                id: "id-card-input",
                name: employeeFilter.employeePID,
                value: `${t("employee.IdCard")}:`,
            },
            {
                id: "phone-input",
                name: employeeFilter.phone,
                value: `${t("employee.Phone")}:`,
            },
            {
                id: "employee-status-input",
                name: handleEmployeeStatusTab(employeeFilter.employeeStatus),
                value: `${t("employee.FilterStatus")}:`,
            },
        ];

        const filteredTabs = filters
            .filter((filter) => filter.name !== null && filter.name !== "")
            .map((filter) => ({ id: filter.id, value: filter.value, name: filter.name }));

        setFilterTabsArray(filteredTabs);
    };

    const removeFilterTab = (tabName: string, tabId: string) => {
        const newArray = [...filterTabsArray];
        const index = newArray.findIndex((tab) => tab.name === tabName);
        newArray.splice(index, 1);
        setFilterTabsArray(newArray);

        switch (tabId) {
            case "keyword-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    keywords: "",
                }));
                break;
            case "first-name-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    firstName: "",
                }));
                break;
            case "last-name-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    lastName: "",
                }));
                break;
            case "department-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    organizationUnitId: null,
                }));
                break;
            case "employee-group-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    employeeGroupId: null,
                }));
                break;
            case "id-card-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    employeePID: "",
                }));
                break;
            case "phone-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    phone: "",
                }));
                break;
            case "employee-status-input":
                setEmployeeFilter((prevData) => ({
                    ...prevData,
                    employeeStatus: null,
                }));
                break;
            default:
                break;
        }
    };

    async function sortBy(sortBy: string) {
        if (employeeFilter.orderBy !== sortBy) {
            setEmployeeFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setEmployeeFilter((prevData) => ({
                ...prevData,
                asc: !employeeFilter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setEmployees([]);
    }

    function getArrowClass(sort: string) {
        if (sort === employeeFilter.orderBy) {
            if (employeeFilter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    async function onClickSearch() {
        setEmployees([]);
        popupService.showLoading();
        filtersStore.setEmployeeFilterData(null);
        setEmployeeFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter((prevCounter) => prevCounter + 1);
    }

    async function getEmployees(): Promise<EmployeeFilterResponse> {
        const filterStore = filtersStore.employeesFilterData;
        if (filterStore != null) {
            if (filterStore.filter?.pageIndex !== 0) {
                if (filterStore.filter) filterStore.filter.pageIndex = 0;
            }
            setEmployeeFilter(filterStore.filter ?? new EmployeeFilterResource());
            setFilterTabsArray(filterStore.filterTabs);
            setHiddenFilter(filterStore.hiddenFilter);
            const responsePromise = api.getEmployees(filterStore.filter ?? new EmployeeFilterResource());
            return (await responsePromise).data;
        }
        const responsePromise = api.getEmployees(employeeFilter);
        await setFilterTabs();
        return (await responsePromise).data;
    }

    const handleScroll = useCallback(() => {
        const employeeContainer = document.getElementById("employee-list-grid");
        const doScroll =
            employeeContainer &&
            employeeContainer.scrollTop + employeeContainer.clientHeight >= employeeContainer.scrollHeight &&
            employeeContainer.scrollTop > 0;
        if (!doScroll || !hasNext) {
            return;
        }
        setEmployeeFilter((prevData) => ({
            ...prevData,
            pageIndex: employeeFilter.pageIndex + 1,
        }));
    }, [employeeFilter.pageIndex, hasNext]);

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.ListOfEmployees)) {
            popupService.close();
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const data = await getEmployees();
            const apiResponse = data;
            setHasNext(apiResponse.hasNext);
            if (employeeFilter.pageIndex === 0) {
                setEmployees(apiResponse.rows);
                setTotal(apiResponse.total);
                setTotalFilter(apiResponse.totalFilter);
            } else {
                setEmployees((oldArray) => [...oldArray, ...apiResponse.rows]);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [employeeFilter.pageIndex, employeeFilter.orderBy, employeeFilter.asc, searchCounter]);

    useEffect(() => {
        let offsetHeight = 99;
        const employeeContainer = document.getElementById("employee-list-grid");
        if (!employeeContainer) {
            return;
        }
        elementIds.forEach((id: string) => {
            const element = document.getElementById(id);
            if (element) {
                offsetHeight += Number(element.clientHeight);
            }
        });
        employeeContainer.style.height = `calc(100vh - ${offsetHeight}px)`;
    });

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            getEmployeesInitData();
        }
        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        const employeeContainer = document.getElementById("employee-list-grid");
        if (!employeeContainer) {
            return;
        }
        employeeContainer.addEventListener("scroll", handleScroll);
        return () => {
            employeeContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        document.addEventListener("keypress", handleKeyPressSearch);
        return () => {
            document.removeEventListener("keypress", handleKeyPressSearch);
        };
    });

    const generateEmployeesXlsx = async () => {
        popupService.showLoading();
        const [err, resp] = await to(api.exportEmployees(employeeFilter));
        if (err) {
            popupService.showApiError(err);
            return;
        }

        const response = resp!;
        if (response.data.success && response.data.interfaceId !== 0) {
            popupService.downloadFile(
                t("general.ExportToXLS?"),
                api.generateEmployeeExportLink(response.data.interfaceId)
            );
        } else {
            popupService.showError("Unhandled exception");
            return;
        }
    };

    const handleCheckAll = () => {
        if (!checkAll) {
            setCheckAll(true);
        } else {
            setCheckAll(false);
        }
        setCheckedEmployees([]);
        setExcludedEmployees([]);
    };

    const handleCheckEmployee = (employeeId: number) => {
        if (checkAll) {
            setExcludedEmployees((prev) => [...prev, employeeId]);
            return;
        }
        setCheckedEmployees((prev) =>
            prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
        );
    };

    const filterRows = hiddenFilter ? (
        <div className="employee-filter-second-row">
            <div className="employee-filter-fields">
                <div>
                    <BaseInput
                        select
                        label={t("employee.EmployeeGroup")}
                        size="small"
                        value={employeeFilter?.employeeGroupId ?? ""}
                        onChange={(event: any) =>
                            setEmployeeFilter((prevData) => ({
                                ...prevData,
                                employeeGroupId: event.target.value,
                            }))
                        }
                        sx={{
                            width: "250px",
                            fontSize: 14,
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    className="select-clear-icon"
                                    sx={{
                                        visibility: employeeFilter?.employeeGroupId ? "visible" : "hidden",
                                    }}
                                    onClick={() =>
                                        setEmployeeFilter((prevData) => ({
                                            ...prevData,
                                            employeeGroupId: null,
                                        }))
                                    }
                                >
                                    <img src={ClearIcon} alt="Clear icon" />
                                </IconButton>
                            ),
                        }}
                    >
                        {employeeGroups.map((employeeGroup) => (
                            <MenuItem key={employeeGroup.id} value={employeeGroup.id}>
                                {employeeGroup.text}
                            </MenuItem>
                        ))}
                    </BaseInput>
                </div>
                <div>
                    <BaseInput
                        label={t("employee.IdCard")}
                        type="search"
                        size="small"
                        value={employeeFilter.employeePID}
                        onChange={(event) =>
                            setEmployeeFilter((prevData) => ({
                                ...prevData,
                                employeePID: event.target.value,
                            }))
                        }
                        sx={{
                            width: "250px",
                        }}
                    />
                </div>
                <div>
                    <BaseInput
                        label={t("employee.Phone")}
                        type="search"
                        size="small"
                        value={employeeFilter.phone}
                        onChange={(event) =>
                            setEmployeeFilter((prevData) => ({
                                ...prevData,
                                phone: event.target.value.replace(/[^\d]/g, "").substring(0, 10),
                            }))
                        }
                        sx={{
                            width: "250px",
                        }}
                    />
                </div>
                <div>
                    <BaseChangeStatus
                        width={"250px"}
                        value={employeeFilter.employeeStatus}
                        title={t("employee.FilterStatus")}
                        updateStatus={(args: boolean | null) =>
                            setEmployeeFilter((prevData) => ({
                                ...prevData,
                                employeeStatus: args,
                            }))
                        }
                    ></BaseChangeStatus>
                </div>
            </div>
        </div>
    ) : (
        ""
    );

    const employeeFilterBlock = (
        <div id="employee-filter-block">
            <div className="employee-header-container">
                <div className="employee-left-element font-style-16"></div>
                <div className="employee-right-element">
                    {checkPermissionsWithoutAlert(PermissionName.ImportEmployees) ? (
                        <BaseTooltip title={t("employee.ImportExportBtn")}>
                            <StyledExcelButton
                                type="button"
                                className="excel-btn"
                                onClick={() => setOpenImportModal(true)}
                            >
                                <i className="icon-f icon-Excel fs-24" />
                            </StyledExcelButton>
                        </BaseTooltip>
                    ) : (
                        ""
                    )}
                    {checkPermissionsWithoutAlert(PermissionName.CreateEmployee) ? (
                        <BaseButton
                            style={{
                                height: "100%",
                                minHeight: "40px",
                            }}
                            className="basic-button"
                            onClick={() => navigate(createEmployeeURL)}
                            type="button"
                        >
                            <div>{t("employee.NewEmpButton")}</div>
                            <img src={PlusElement} alt="PlusElement" />
                        </BaseButton>
                    ) : (
                        ""
                    )}
                </div>
            </div>
            <div className="employee-filter">
                <div className="font-style employee-span">{t("employee.FilterHeader")}</div>
                <div className="employee-filter-first-row">
                    <div className="employee-filter-fields">
                        <div>
                            <BaseInput
                                id="keyword-input"
                                className="search-input"
                                placeholder={`${t("employee.FilterSearch")}`}
                                type="search"
                                size="small"
                                value={employeeFilter.keywords}
                                onChange={(event) =>
                                    setEmployeeFilter((prevData) => ({
                                        ...prevData,
                                        keywords: event.target.value,
                                    }))
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: "397px",
                                }}
                            />
                        </div>
                        <div>
                            <BaseInput
                                id="first-name-input"
                                label={t("employee.FirstName")}
                                type="search"
                                size="small"
                                value={employeeFilter.firstName}
                                onChange={(event) =>
                                    setEmployeeFilter((prevData) => ({
                                        ...prevData,
                                        firstName: event.target.value,
                                    }))
                                }
                                sx={{
                                    width: "200px",
                                }}
                            />
                        </div>
                        <div>
                            <BaseInput
                                id="last-name-input"
                                label={t("employee.LastName")}
                                type="search"
                                size="small"
                                value={employeeFilter.lastName}
                                onChange={(event) =>
                                    setEmployeeFilter((prevData) => ({
                                        ...prevData,
                                        lastName: event.target.value,
                                    }))
                                }
                                sx={{
                                    width: "200px",
                                }}
                            />
                        </div>
                        <div>
                            <BaseInput
                                select
                                id="department-input"
                                label={t("employee.Department")}
                                size="small"
                                value={employeeFilter?.organizationUnitId ?? ""}
                                onChange={(event: any) =>
                                    setEmployeeFilter((prevData) => ({
                                        ...prevData,
                                        organizationUnitId: event.target.value,
                                    }))
                                }
                                sx={{
                                    width: "200px",
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            className="select-clear-icon"
                                            sx={{
                                                visibility: employeeFilter?.organizationUnitId ? "visible" : "hidden",
                                            }}
                                            onClick={() =>
                                                setEmployeeFilter((prevData) => ({
                                                    ...prevData,
                                                    organizationUnitId: null,
                                                }))
                                            }
                                        >
                                            <img src={ClearIcon} alt="Clear icon" />
                                        </IconButton>
                                    ),
                                }}
                            >
                                {organizationUnits.map((organizationUnit) => (
                                    <MenuItem key={organizationUnit.id} value={organizationUnit.id}>
                                        {organizationUnit.text}
                                    </MenuItem>
                                ))}
                            </BaseInput>
                        </div>
                        <div>
                            <BaseTooltip
                                title={!hiddenFilter ? t("employee.FilterButtonShow") : t("employee.FilterButtonHide")}
                                placement="bottom"
                            >
                                <IconButton
                                    className="show-hidden-filter-button-div"
                                    onClick={() => setHiddenFilter(!hiddenFilter)}
                                    type="button"
                                >
                                    {hiddenFilter ? (
                                        <KeyboardDoubleArrowUpIcon className="show-hidden-filter-button" />
                                    ) : (
                                        <KeyboardDoubleArrowDownIcon className="show-hidden-filter-button" />
                                    )}
                                </IconButton>
                            </BaseTooltip>
                        </div>
                    </div>
                    <div className="filter-search-btn">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <span
                                onClick={handleClearFilters}
                                className="link-element clear-all-btn"
                                style={{
                                    minWidth: "70px",
                                }}
                            >
                                {t("display.ClearAll")}
                            </span>
                        </div>
                        <BaseButton
                            style={{ padding: "0px 30px" }}
                            className="basic-button"
                            onClick={() => onClickSearch()}
                        >
                            {t("general.filter")}
                        </BaseButton>
                    </div>
                </div>
                {filterRows}
            </div>
        </div>
    );

    const employeeInfo = (
        <div id="employee-info-tab" className="employee-info-tab-container">
            <EmployeeInfoTab
                selectedEmployee={selectedEmployee}
                employeeFilter={employeeFilter}
                filterTabsArray={filterTabsArray}
                hiddenFilter={hiddenFilter}
            />
            <div className="employee-info-tab-right">
                <BaseButton type="button" className="basic-button" onClick={() => setSelectedEmployee(null)}>
                    <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                    <div>{t("employee.BackToList")}</div>
                </BaseButton>
            </div>
        </div>
    );

    const filterTabs = !selectedEmployee ? (
        <div
            id="filter-tabs"
            className="filter-tab-container"
            style={filterTabsArray.length > 0 ? { marginTop: "10px" } : { marginTop: "0px" }}
        >
            {filterTabsArray.map((filterTab) => (
                <div key={filterTab.id} className="filter-tab-element font-style">
                    <span>
                        {filterTab.value}
                        <span className="tab-color">{filterTab.name}</span>
                    </span>
                    <button className="filter-tab-button" onClick={() => removeFilterTab(filterTab.name, filterTab.id)}>
                        <FilterCancelIcon />
                    </button>
                </div>
            ))}
        </div>
    ) : (
        ""
    );

    const filterOrEmployee = selectedEmployee ? employeeInfo : employeeFilterBlock;

    const addImportModal = openImportModal ? (
        <div>
            <Modal
                open={openImportModal}
                onClose={() => setOpenImportModal(false)}
                sx={{ backdropFilter: "blur(6px)" }}
            >
                <div className="new-forms-modal-style">
                    <div className="new-forms-modal-header">
                        <CancelIcon
                            onClick={() => setOpenImportModal(false)}
                            style={{ color: "#586A84", cursor: "pointer" }}
                        />
                    </div>
                    <div
                        className={`new-forms-modal-content employee-import-export-modal-content ${
                            checkPermissionsWithoutAlert(PermissionName.ImportEmployees) ? "" : "without-import"
                        }`}
                    >
                        {checkPermissionsWithoutAlert(PermissionName.ImportEmployees) ? (
                            <div onClick={() => navigate("/employees/import")} className="new-forms-modal-button">
                                <div className="new-forms-modal-button-content">
                                    <div id="new-forms-modal-icon" className="new-forms-modal-icon">
                                        <i className="icon-f icon-import fs-20" />
                                    </div>
                                    <div>{t("employee.import.importTitle")}</div>
                                </div>
                            </div>
                        ) : (
                            ""
                        )}
                        <div onClick={() => generateEmployeesXlsx()} className="new-forms-modal-button">
                            <div className="new-forms-modal-button-content">
                                <div id="new-forms-modal-icon" className="new-forms-modal-icon ">
                                    <i className="icon-f icon-export fs-20" />
                                </div>
                                <div>{t("employee.import.exportTitle")}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    ) : (
        ""
    );

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="employee-container">
                {filterOrEmployee}
                {filterTabs}
                <div>
                    <div id="employee-total-count">
                        <TotalCountComponent
                            title={t("employees.QuantityOfEmployees") as string}
                            allCount={total}
                            currentCount={totalFilter}
                        />
                        <EmployeesActionButton
                            checkAll={checkAll}
                            selectedEmployees={checkedEmployees}
                            excludedEmployees={excludedEmployees}
                            handleClearFilters={handleClearFilters}
                            filterRequest={employeeFilter}
                        />
                    </div>
                    <div id="employee-list-grid" className="employee-list-grid">
                        <div className="fake-thead"></div>
                        <table>
                            <thead>
                                <tr>
                                    <td style={{ width: "3%" }}>
                                        <Checkbox
                                            checked={checkAll}
                                            indeterminate={
                                                checkedEmployees.length > 0 && checkedEmployees.length < total
                                            }
                                            onChange={handleCheckAll}
                                        />
                                    </td>
                                    <td onClick={() => sortBy("Number")}>
                                        <span>
                                            {t("employee.Number")}
                                            <i className={`icon-f ${getArrowClass("Number")} fs-12`} />
                                        </span>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("employee.IdCard")}</div>
                                    </td>
                                    <td onClick={() => sortBy("First Name")}>
                                        <span>
                                            {t("employee.FirstName")}
                                            <i className={`icon-f ${getArrowClass("First Name")} fs-12`} />
                                        </span>
                                    </td>
                                    <td onClick={() => sortBy("Last Name")}>
                                        <span>
                                            {t("employee.LastName")}
                                            <i className={`icon-f ${getArrowClass("Last Name")} fs-12`} />
                                        </span>
                                    </td>
                                    <td>{t("employee.Department")}</td>
                                    <td>{t("employee.Group")}</td>
                                    <td onClick={() => sortBy("Status")}>
                                        <span>
                                            {t("employee.Status")}
                                            <i className={`icon-f ${getArrowClass("Status")} fs-12`} />
                                        </span>
                                    </td>
                                    <td onClick={() => sortBy("WorkStartDate")}>
                                        <div className="table-number-format-name">
                                            <span>
                                                {t("employee.WorkStartDate")}
                                                <i className={`icon-f ${getArrowClass("WorkStartDate")} fs-12`} />
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("employee.Phone")}</div>
                                    </td>
                                    <td>{t("employee.Email")}</td>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map((employee) => (
                                    <tr
                                        id="employees-table-row"
                                        key={employee.id}
                                        onClick={() => setSelectedEmployee(employee)}
                                        className={
                                            selectedEmployee && employee.id === selectedEmployee.id
                                                ? "selectedEmployee"
                                                : ""
                                        }
                                    >
                                        <td style={{ width: "3%" }}>
                                            <Checkbox
                                                checked={
                                                    (checkAll && !excludedEmployees.includes(employee.id)) ||
                                                    checkedEmployees.includes(employee.id)
                                                }
                                                onChange={() => handleCheckEmployee(employee.id)}
                                            />
                                        </td>
                                        <td>
                                            <div
                                                onClick={() =>
                                                    openEmployeePage(
                                                        employee?.id,
                                                        employee?.firstName + " " + employee?.lastName
                                                    )
                                                }
                                                className="link-element"
                                            >
                                                {employee.employeeNumber}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">{employee.employeePID}</div>
                                        </td>
                                        <td>
                                            <div className="single-ellipsis" style={{ maxWidth: "200px" }}>
                                                {employee.firstName}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="single-ellipsis" style={{ maxWidth: "200px" }}>
                                                {employee.lastName}
                                            </div>
                                        </td>
                                        <td>{employee.organizationUnit}</td>
                                        <td>{employee.employeeGroup}</td>
                                        <td>
                                            {employee.isActive ? (
                                                <div className="status-element">
                                                    <div className="status-color-active"></div>
                                                    <div>{t("employee.ActiveStatus")}</div>
                                                </div>
                                            ) : (
                                                <div className="status-element">
                                                    <div className="status-color-inactive"></div>
                                                    <div>{t("employee.InactiveStatus")}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">
                                                {employee.workStartDate
                                                    ? formatDate(String(employee.workStartDate))
                                                    : ""}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">{employee.mobilePhone}</div>
                                        </td>
                                        <td>
                                            <div onClick={() => sendEmail(employee.email)} className="link-element">
                                                {employee.email}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {addImportModal}
        </CacheProvider>
    );
};

export default Employees;
